<div class="flex flex-col gap-6 animate-fade-in">

  <!-- Header Section -->
  <div class="flex justify-between items-end">
    <div>
      <h1 class="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-[var(--text-muted)]">System Monitor</h1>
      <p class="text-[var(--text-muted)] mt-1">Real-time infrastructure health and telemetry data.</p>
    </div>
    
    <div class="flex items-center gap-2">
      <span class="relative flex h-3 w-3">
        <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
        <span class="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
      </span>
      <span class="text-xs font-semibold text-green-400">All Systems Operational</span>
    </div>
  </div>

  <!-- Real-time Metrics Grid -->
  <div class="grid grid-cols-2 lg:grid-cols-4 gap-6">
    
    <!-- CPU Usage -->
    <div class="bg-[var(--bg-panel)] border border-[#ffffff10] rounded-xl p-6 relative overflow-hidden group hover:border-[#506ef7]/50 transition-colors">
      <div class="flex justify-between items-center mb-4 relative z-10">
        <h3 class="text-[var(--text-muted)] font-medium text-sm">CPU Usage</h3>
        <svg class="w-5 h-5 text-[#506ef7]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"></path></svg>
      </div>
      <div class="text-3xl font-bold text-white relative z-10">{{ cpuUsage | number:'1.0-0' }}%</div>
      
      <!-- Progress Bar -->
      <div class="w-full h-1.5 bg-[#ffffff10] rounded-full mt-4 overflow-hidden relative z-10">
        <div class="h-full bg-gradient-to-r from-[#506ef7] to-blue-400 transition-all duration-1000 ease-out" [style.width.%]="cpuUsage"></div>
      </div>
      
      <!-- Ambient Glow -->
      <div class="absolute -bottom-6 -right-6 w-24 h-24 bg-[#506ef7]/20 rounded-full blur-2xl group-hover:bg-[#506ef7]/30 transition-colors"></div>
    </div>

    <!-- Memory Usage -->
    <div class="bg-[var(--bg-panel)] border border-[#ffffff10] rounded-xl p-6 relative overflow-hidden group hover:border-indigo-500/50 transition-colors">
      <div class="flex justify-between items-center mb-4 relative z-10">
        <h3 class="text-[var(--text-muted)] font-medium text-sm">Memory</h3>
        <svg class="w-5 h-5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path></svg>
      </div>
      <div class="text-3xl font-bold text-white relative z-10">{{ memUsage | number:'1.0-0' }}%</div>
      
      <div class="w-full h-1.5 bg-[#ffffff10] rounded-full mt-4 overflow-hidden relative z-10">
        <div class="h-full bg-gradient-to-r from-indigo-500 to-indigo-400 transition-all duration-1000 ease-out" [style.width.%]="memUsage"></div>
      </div>
      <div class="absolute -bottom-6 -right-6 w-24 h-24 bg-indigo-500/20 rounded-full blur-2xl group-hover:bg-indigo-500/30 transition-colors"></div>
    </div>

    <!-- Storage -->
    <div class="bg-[var(--bg-panel)] border border-[#ffffff10] rounded-xl p-6 relative overflow-hidden group hover:border-emerald-500/50 transition-colors">
      <div class="flex justify-between items-center mb-4 relative z-10">
        <h3 class="text-[var(--text-muted)] font-medium text-sm">Storage</h3>
        <svg class="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"></path></svg>
      </div>
      <div class="text-3xl font-bold text-white relative z-10">{{ diskUsage }}%</div>
      
      <div class="w-full h-1.5 bg-[#ffffff10] rounded-full mt-4 overflow-hidden relative z-10">
        <div class="h-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all duration-1000 ease-out" [style.width.%]="diskUsage"></div>
      </div>
      <div class="absolute -bottom-6 -right-6 w-24 h-24 bg-emerald-500/20 rounded-full blur-2xl group-hover:bg-emerald-500/30 transition-colors"></div>
    </div>

    <!-- Network -->
    <div class="bg-[var(--bg-panel)] border border-[#ffffff10] rounded-xl p-6 relative overflow-hidden group hover:border-blue-500/50 transition-colors">
      <div class="flex justify-between items-center mb-4 relative z-10">
        <h3 class="text-[var(--text-muted)] font-medium text-sm">Network Tx/Rx</h3>
        <svg class="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path></svg>
      </div>
      <div class="flex items-baseline gap-1 relative z-10">
        <span class="text-3xl font-bold text-white">{{ netTraffic | number:'1.1-1' }}</span>
        <span class="text-sm font-medium text-[var(--text-muted)]">GB/s</span>
      </div>
      
      <div class="w-full h-1.5 bg-[#ffffff10] rounded-full mt-4 overflow-hidden relative z-10">
        <div class="h-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all duration-1000 ease-out" style="width: 75%"></div>
      </div>
      <div class="absolute -bottom-6 -right-6 w-24 h-24 bg-blue-500/20 rounded-full blur-2xl group-hover:bg-blue-500/30 transition-colors"></div>
    </div>

  </div>

  <!-- Live Action Logs -->
  <div class="bg-[var(--bg-panel)] border border-[#ffffff10] rounded-xl flex flex-col flex-1 mt-2 mb-4 overflow-hidden shadow-lg shadow-black/20 relative">
    
    <div class="p-4 border-b border-[#ffffff10] bg-[#ffffff05] flex justify-between items-center">
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
        <h2 class="text-sm font-semibold text-white tracking-widest uppercase">Live Telemetry</h2>
      </div>
      
      <div class="flex gap-2">
        <button class="px-3 py-1 bg-white/5 border border-white/10 rounded hover:bg-white/10 text-xs text-white transition-colors">Export</button>
        <button class="p-1 px-3 bg-[#506ef7]/10 text-[#506ef7] border border-[#506ef7]/20 rounded hover:bg-[#506ef7]/20 text-xs transition-colors flex items-center gap-1">
          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          Filter
        </button>
      </div>
    </div>

    <!-- Terminal/Log window -->
    <div class="bg-[#0a0a0a] p-4 font-mono text-sm h-80 overflow-y-auto space-y-2">
      @for (log of logs; track log.time) {
        <div class="flex items-start gap-4 p-1 hover:bg-[#ffffff05] rounded group transition-colors">
          <span class="text-gray-500 w-16 flex-shrink-0 group-hover:text-gray-400">{{ log.time }}</span>
          
          <span class="w-16 font-semibold flex-shrink-0" [ngClass]="getLogClass(log.level)">
            [{{ log.level }}]
          </span>
          
          <span class="text-gray-400 w-12 flex-shrink-0">&lt;{{ log.source }}&gt;</span>
          
          <span class="text-gray-300 break-all">{{ log.message }}</span>
        </div>
      }
      <div class="flex items-center gap-4 p-1">
        <span class="text-gray-600 block w-4 animate-pulse">_</span>
      </div>
    </div>

  </div>

</div>
